import { forkJoin, of } from 'rxjs';
// Angular
import { Injectable } from '@angular/core';
// RxJS
import { map, mergeMap, catchError } from 'rxjs/operators';
// NGRX
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
// CRUD
import { LayoutUtilsService, MessageType, QueryParamsModel } from '../../_base/crud';

// State
import { AppState } from '../../reducers';

import { Router } from '@angular/router';
import {
  ClearPatientInsuranceErrors,
  PatientInsuranceActionToggleLoading,
  PatientInsuranceActionTypes,
  PatientInsuranceCreated,
  PatientInsuranceDeleted,
  PatientInsuranceOnServerCreated,
  PatientInsuranceOnServerDeleted,
  PatientInsuranceOnServerUpdated,
  PatientInsurancePageCancelled,
  PatientInsurancePageLoaded,
  PatientInsurancePageRequested,
  PatientInsurancePageToggleLoading,
  PatientInsuranceUpdated,
  ThrowPatientInsuranceError,
} from '../_actions/patient-insurance.actions';
import { PatientInsuranceService } from '../_services';
import { Update } from '@ngrx/entity';
import { PatientInsuranceModel } from '../_models/patient-insurance.model';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class PatientInsuranceEffects {
  showPageLoadingDistpatcher = new PatientInsurancePageToggleLoading({ isLoading: true });
  showActionLoadingDistpatcher = new PatientInsuranceActionToggleLoading({ isLoading: true });
  hideActionLoadingDistpatcher = new PatientInsuranceActionToggleLoading({ isLoading: false });

  @Effect()
  loadPatientInsurancePage$ = this.actions$.pipe(
    ofType<PatientInsurancePageRequested>(
      PatientInsuranceActionTypes.PatientInsurancePageRequested,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showPageLoadingDistpatcher);
      const requestToServer = this.patientsInsuranceService.findPatientInsurance(
        payload.page,
        payload.patientId,
      );
      const lastQuery = of(payload.page);
      return forkJoin(requestToServer, lastQuery).pipe(
        catchError((error) => {
          return of([error]);
        }),
        map((response) => {
          if (response[0]?.error) {
            if (response[0].status === 401) {
              return response[0];
            } else if (response[0].status === 403) {
              this.router.navigateByUrl('/error/403');
              return new PatientInsurancePageCancelled();
            } else {
              this.store.dispatch(new ThrowPatientInsuranceError({ error: response[0].error }));
              return new PatientInsurancePageCancelled();
            }
          } else {
            const result = response[0];
            // tslint:disable-next-line:no-shadowed-variable
            const lastQuery: QueryParamsModel = response[1];
            this.store.dispatch(new ClearPatientInsuranceErrors());
            const mappedResult = Array.isArray(result?.data) ? result.data : [];
            return new PatientInsurancePageLoaded({
              patientInsurance: mappedResult,
              totalCount: result?.count ?? mappedResult.length,
              page: lastQuery,
            });
          }
        }),
      );
    }),
  );

  @Effect()
  deletePatientInsurance$ = this.actions$.pipe(
    ofType<PatientInsuranceOnServerDeleted>(
      PatientInsuranceActionTypes.PatientInsuranceOnServerDeleted,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.patientsInsuranceService.deletePatientInsurance(payload.patientInsuranceId).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowPatientInsuranceError({ error: res.error });
          } else {
            this.store.dispatch(new ClearPatientInsuranceErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            const message = this.translate.instant('PATIENT.INPUT.INSURANCE.DELETED_MESSAGE');
            this.layoutUtilsService.showActionNotification(
              message,
              MessageType.Update,
              3000,
              false,
              false,
            );

            return new PatientInsuranceDeleted(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  updatePatientInsurance$ = this.actions$.pipe(
    ofType<PatientInsuranceOnServerUpdated>(
      PatientInsuranceActionTypes.PatientInsuranceOnServerUpdated,
    ),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.patientsInsuranceService.updatePatientInsurance(payload.patientInsurance).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res: any) => {
          if (res && res.error) {
            return new ThrowPatientInsuranceError({ error: res.error });
          } else {
            this.store.dispatch(new ClearPatientInsuranceErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            const updatePatientInsurance: Update<PatientInsuranceModel> = {
              id: payload.patientInsurance.id,
              changes: payload.patientInsurance,
            };
            const message = this.translate.instant('PATIENT.INPUT.INSURANCE.UPDATED_MESSAGE');
            this.layoutUtilsService.showActionNotification(
              message,
              MessageType.Update,
              3000,
              false,
              false,
            );
            return new PatientInsuranceUpdated({
              partialPatientInsurance: updatePatientInsurance,
              patientInsurance: payload.patientInsurance,
            });
          }
        }),
      );
    }),
  );

  @Effect()
  createPatientInsurance$ = this.actions$.pipe(
    ofType<PatientInsuranceOnServerCreated>(
      PatientInsuranceActionTypes.PatientInsuranceOnServerCreated,
    ),
    mergeMap(({ payload }) => {
      // this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.patientsInsuranceService
        .createPatientInsurance(payload.patientInsurance, payload.patientId)
        .pipe(
          catchError((error) => {
            return of(error);
          }),
          map((res) => {
            if (res && res.error) {
              return new ThrowPatientInsuranceError({ error: res });
            } else {
              this.store.dispatch(new ClearPatientInsuranceErrors());
              this.store.dispatch(this.hideActionLoadingDistpatcher);
              res.data.insurance = payload.patientInsurance.insurance;
              const message = this.translate.instant('PATIENT.INPUT.INSURANCE.CREATED_MESSAGE');
              this.layoutUtilsService.showActionNotification(
                message,
                MessageType.Update,
                3000,
                false,
                false,
              );
              return new PatientInsuranceCreated({ patientInsurance: res.data });
            }
          }),
        );
    }),
  );

  constructor(
    private actions$: Actions,
    private patientsInsuranceService: PatientInsuranceService,
    private store: Store<AppState>,
    private router: Router,
    private layoutUtilsService: LayoutUtilsService,
    private translate: TranslateService,
  ) {}
}
